import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import StopIcon from "@mui/icons-material/Stop";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import html2canvas from "html2canvas";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SnackBar from "../../component/SnackBar";
import {
  FeedBackButton,
  SubmitFeedback,
  TranscribeAudio,
} from "../../redux/action/Feedback";
import Draggable from "react-draggable";
import { HandleSkeletion } from "../../utils/constants";

const mimeToExtension = {
  "video/webm": "webm",
  "video/mp4": "mp4",
  "audio/webm": "webm",
  "audio/mp4": "m4a",
  "audio/wav": "wav",
};

const FloatingFeedbackButton = () => {
  const dispatch = useDispatch();
  const [screenshot, setScreenshot] = useState(null);
  const [video, setVideo] = useState(null);
  const [isRetaking, setIsRetaking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [voiceRecordingDuration, setVoiceRecordingDuration] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [formData, setFormData] = useState({
    description: "",
  });
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  const open = useSelector((state) => state.feedback.open);

  const mediaRecorderRef = useRef(null);
  const videoStreamRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const voiceTimerRef = useRef(null);
  const voiceStreamRef = useRef(null);
  const MAX_DURATION = 30;
  const MAX_VOICE_DURATION = 30;
  const MAX_COMMENT_LENGTH = 300;

  // Helper function to get supported mime type
  const getSupportedMimeType = (type) => {
    const mimeTypes = {
      video: [
        "video/webm;codecs=vp9",
        "video/webm;codecs=vp8",
        "video/webm",
        "video/mp4",
      ],
      audio: ["audio/webm", "audio/mp4", "audio/wav"],
    };

    return (
      mimeTypes[type].find((mimeType) =>
        MediaRecorder.isTypeSupported(mimeType)
      ) || ""
    );
  };

  // Check browser compatibility
  const checkBrowserCompatibility = () => {
    const isMediaRecorderSupported = typeof MediaRecorder !== "undefined";
    const isGetUserMediaSupported = !!(
      navigator.mediaDevices && navigator.mediaDevices.getUserMedia
    );
    const isGetDisplayMediaSupported = !!(
      navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia
    );

    return {
      isMediaRecorderSupported,
      isGetUserMediaSupported,
      isGetDisplayMediaSupported,
      isFullySupported:
        isMediaRecorderSupported &&
        isGetUserMediaSupported &&
        isGetDisplayMediaSupported,
    };
  };

  const startVideoRecording = async () => {
    const compatibility = checkBrowserCompatibility();
    if (!compatibility.isFullySupported) {
      setSnackbarState({
        open: true,
        severity: "error",
        message:
          "Screen recording is not supported in your browser. Please use Chrome, Firefox, or Edge.",
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: 1280,
          height: 720,
          frameRate: 30,
        },
        audio: true,
      });
      videoStreamRef.current = stream;

      const mimeType = getSupportedMimeType("video");
      if (!mimeType) {
        throw new Error("No supported video mime type found");
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 2500000,
      });

      const chunks = [];
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        if (blob.size > 10 * 1024 * 1024) {
          setSnackbarState({
            open: true,
            severity: "error",
            message: "Video size exceeds 10MB limit",
          });
          return;
        }
        setVideo(URL.createObjectURL(blob));
        setScreenshot(null);
        dispatch(FeedBackButton(true));
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      // Start the timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((prev) => {
          const newDuration = prev + 1;
          if (newDuration >= MAX_DURATION) {
            clearInterval(recordingTimerRef.current);
            stopVideoRecording();
            return MAX_DURATION;
          }
          return newDuration;
        });
      }, 1000);

      // Handle when user stops sharing screen
      stream.getVideoTracks()[0].onended = () => {
        stopVideoRecording();
      };
    } catch (error) {
      setSnackbarState({
        open: true,
        severity: "error",
        message: error.message || "Failed to start screen recording",
      });
    }
  };

  const stopVideoRecording = () => {
    if (mediaRecorderRef.current) {
      // First stop the timer
      clearInterval(recordingTimerRef.current);
      setRecordingDuration(0);

      // Then stop the recording
      if (mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }

      // Finally stop all tracks
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach((track) => {
          track.stop();
          track.onended = null; // Remove the onended handler
        });
      }

      setIsRecording(false);
    }
  };

  const captureScreenshot = async () => {
    try {
      const chatbotElements = document.querySelectorAll(".MuiTypography-root"); // Adjust selector
      chatbotElements.forEach((el) => {
        el.style.opacity = "1";
        el.style.visibility = "visible";
        el.style.transform = "none";
        el.style.position = "relative"; // Reset positioning if needed
        el.style.fontFamily = "'Inter', sans-serif"; // Match your font
      });

      const screenshotData = await html2canvas(document.body, {
        useCORS: true, // For external resources
        scale: 2, // Higher resolution
        logging: true, // Debug issues
        ignoreElements: (el) => el.classList?.contains("js-warning"), // Exclude warnings
      });

      // Revert styles if needed
      chatbotElements.forEach((el) => {
        el.style.opacity = "";
        el.style.visibility = "";
        el.style.transform = "";
        el.style.position = "";
      });
      setScreenshot(screenshotData.toDataURL("image/png"));
      setVideo(null);
      setIsRetaking(false);
      setTimeout(async () => {
        dispatch(FeedBackButton(true));
      }, 50);
    } catch (error) {
      setSnackbarState({
        open: true,
        severity: "error",
        message: "Failed to capture screenshot",
      });
    }
  };

  const handleOpen = async () => {
    if (isRetaking) {
      await captureScreenshot();
    } else if (isRecording) {
      stopVideoRecording();
    } else {
      await captureScreenshot();
    }
  };

  const handleClose = () => {
    dispatch(FeedBackButton(false));
    setScreenshot(null);
    setVideo(null);
    setIsRetaking(false);
    setIsSubmitting(false);
    setFormData({
      description: "",
    });
  };

  const handleRetakeScreenshot = () => {
    dispatch(FeedBackButton(false));
    setIsRetaking(true);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("description", formData.description);
      formDataToSend.append("issue_page", window.location.href);

      if (screenshot) {
        const screenshotResponse = await fetch(screenshot);
        const blob = await screenshotResponse.blob();
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        formDataToSend.append(
          "screenshot",
          blob,
          `screenshot-${timestamp}.png`
        );
        formDataToSend.append("file_type", "screenshot");
      }

      if (video) {
        const videoResponse = await fetch(video);
        const blob = await videoResponse.blob();
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const mimeType = blob.type.split(";")[0];
        const ext = mimeToExtension[mimeType] || "webm";
        formDataToSend.append("video", blob, `recording-${timestamp}.${ext}`);
        formDataToSend.append("file_type", "video");
      }

      await SubmitFeedback(formDataToSend);

      setSnackbarState({
        open: true,
        severity: "success",
        message: "Feedback submitted successfully!",
      });
      handleClose();
    } catch (error) {
      setSnackbarState({
        open: true,
        severity: "error",
        message: error.message || "Failed to submit feedback",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseValidation = () => {
    setSnackbarState((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const startVoiceRecording = async () => {
    const compatibility = checkBrowserCompatibility();
    if (!compatibility.isGetUserMediaSupported) {
      setSnackbarState({
        open: true,
        severity: "error",
        message:
          "Voice recording is not supported in your browser. Please use Chrome, Firefox, or Edge.",
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      voiceStreamRef.current = stream;

      const mimeType = getSupportedMimeType("audio");
      if (!mimeType) {
        throw new Error("No supported audio mime type found");
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
      });

      const chunks = [];
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        mediaRecorderRef.current.recordedBlob = blob;
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsVoiceRecording(true);
      setVoiceRecordingDuration(0);

      voiceTimerRef.current = setInterval(() => {
        setVoiceRecordingDuration((prev) => {
          const newDuration = prev + 1;
          if (newDuration >= MAX_VOICE_DURATION) {
            clearInterval(voiceTimerRef.current);
            stopVoiceRecording();
            return MAX_VOICE_DURATION;
          }
          return newDuration;
        });
      }, 1000);
    } catch (error) {
      setSnackbarState({
        open: true,
        severity: "error",
        message: error.message || "Failed to start voice recording",
      });
    }
  };

  const stopVoiceRecording = async () => {
    if (mediaRecorderRef.current) {
      // Stop the timer immediately
      clearInterval(voiceTimerRef.current);
      setVoiceRecordingDuration(0);
      setIsVoiceRecording(false);

      if (mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }

      // Wait for the onstop event to complete
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Send the request only after stopping
      if (mediaRecorderRef.current.recordedBlob) {
        setIsProcessingVoice(true);
        try {
          const formData = new FormData();
          const audioBlob = mediaRecorderRef.current.recordedBlob;
          const audioMimeType = audioBlob.type.split(";")[0];
          const audioExt = mimeToExtension[audioMimeType] || "webm";
          formData.append("audio_file", audioBlob, `recording.${audioExt}`);

          const data = await TranscribeAudio(formData);

          if (data.success) {
            setFormData((prev) => {
              const newText =
                prev.description + (prev.description ? " " : "") + data.text;
              // Truncate the text if it exceeds MAX_COMMENT_LENGTH
              const truncatedText = newText.slice(0, MAX_COMMENT_LENGTH);

              // Show notification if text was truncated
              if (newText.length > MAX_COMMENT_LENGTH) {
                setSnackbarState({
                  open: true,
                  severity: "warning",
                  message: `Transcribed text was truncated to fit the ${MAX_COMMENT_LENGTH} character limit`,
                });
              }

              return {
                ...prev,
                description: truncatedText,
              };
            });
          } else {
            setSnackbarState({
              open: true,
              severity: "error",
              message: "Failed to transcribe audio",
            });
          }
        } catch (error) {
          setSnackbarState({
            open: true,
            severity: "error",
            message: "Failed to transcribe audio",
          });
        } finally {
          setIsProcessingVoice(false);
        }
      }

      // Clean up
      if (voiceStreamRef.current) {
        voiceStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (voiceTimerRef.current) {
        clearInterval(voiceTimerRef.current);
      }
      if (mediaRecorderRef.current) {
        stopVideoRecording();
      }
      if (audioRecorderRef.current) {
        stopVoiceRecording();
      }
    };
  }, []);
  useEffect(() => {
    if (open && video === null && screenshot === null) {
      captureScreenshot();
    }
  }, [open]);

  // Handle browser stop
  useEffect(() => {
    if (videoStreamRef.current) {
      const track = videoStreamRef.current.getVideoTracks()[0];
      if (track) {
        track.onended = () => {
          stopVideoRecording();
        };
      }
    }
  }, [videoStreamRef.current]);

  return (
    <Fragment>
      {(isRetaking || isRecording) && (
        <Draggable handle=".drag-handle">
          <Box
            className="drag-handle"
            sx={{
              position: "fixed",
              top: "4rem",
              right: "2.3rem",
              cursor: "move",
              zIndex: 1000,
            }}
          >
            <Fragment>
              <Box
                position="fixed"
                top="-3rem"
                right="0.5rem"
                width={"max-content"}
                backgroundColor="rgba(0, 0, 0, 0.7)"
                color="white"
                padding="8px 16px"
                borderRadius="20px"
              >
                <Typography variant="body2">
                  {isRecording
                    ? `Recording: ${formatDuration(recordingDuration)}`
                    : "Take ScreenShot"}
                </Typography>
              </Box>

              <Fab
                color="primary"
                aria-label={
                  isRetaking
                    ? "capture-screenshot"
                    : isRecording
                    ? "stop-recording"
                    : "feedback"
                }
                onClick={handleOpen}
              >
                {isRetaking ? (
                  <CameraAltIcon />
                ) : isRecording ? (
                  <StopIcon />
                ) : (
                  ""
                )}
              </Fab>
            </Fragment>
          </Box>
        </Draggable>
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        sx={{
          zIndex: 10000,
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Submit Feedback
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={handleClose}
              disabled={isSubmitting || screenshot === null || !video === null}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={!formData.description || isSubmitting}
              loading={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Comments"
              fullWidth
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= MAX_COMMENT_LENGTH) {
                  setFormData({ ...formData, description: value });
                }
              }}
              helperText={`${formData.description.length}/${MAX_COMMENT_LENGTH} characters`}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ alignSelf: "flex-start", mt: 1 }}
                  >
                    <IconButton
                      color={
                        isVoiceRecording
                          ? "error"
                          : isProcessingVoice
                          ? "default"
                          : "primary"
                      }
                      onClick={
                        isVoiceRecording
                          ? stopVoiceRecording
                          : startVoiceRecording
                      }
                      disabled={isProcessingVoice}
                      size="small"
                    >
                      {isVoiceRecording ? (
                        <MicOffIcon />
                      ) : isProcessingVoice ? (
                        <CircularProgress size={24} />
                      ) : (
                        <MicIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {isVoiceRecording && (
              <Typography variant="body2" color="text.secondary">
                Recording: {formatDuration(voiceRecordingDuration)} /{" "}
                {MAX_VOICE_DURATION}s
              </Typography>
            )}
            {isProcessingVoice && (
              <Typography variant="body2" color="text.secondary">
                Processing voice recording...
              </Typography>
            )}
            {screenshot || video ? (
              <Box sx={{ mt: 2, position: "relative" }}>
                {screenshot && (
                  <img
                    src={screenshot}
                    alt="Screenshot"
                    style={{ maxWidth: "100%", height: "auto" }}
                  />
                )}
                {video && (
                  <video
                    src={video}
                    controls
                    style={{ maxWidth: "100%", height: "auto" }}
                  />
                )}
                <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleMenuClick}
                    startIcon={<CameraAltIcon />}
                    endIcon={<ArrowDropDownIcon />}
                  >
                    Retake
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    disablePortal
                    container={anchorEl?.closest(".MuiDialog-root")}
                  >
                    <MenuItem
                      onClick={() => {
                        handleMenuClose();
                        handleRetakeScreenshot();
                      }}
                    >
                      Retake Screenshot
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleMenuClose();
                        dispatch(FeedBackButton(false));
                        startVideoRecording();
                      }}
                    >
                      Record Video
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>
            ) : (
              <HandleSkeletion height={100} />
            )}
          </Box>
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>

      <SnackBar
        snackbarState={snackbarState}
        handleCloseValidation={handleCloseValidation}
      />
    </Fragment>
  );
};

export default FloatingFeedbackButton;
